import React from 'react';
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import GridTable from '../../components/GridTable';
import Layout from '../../components/Layout';
import CustomImage from '../../components/CustomImage';
import { MODULES } from '../../utils/constants';
import { omitEmptyOptionalFields, uploadFile } from '../../utils/helper';
import { fetchCamps } from '../../services/camps';
import {
    fetchExternalCatalogRows,
    updateCorporateCatalogMetadata,
    updatePublicCatalogMetadata,
} from '../../services/externalCatalog';
import { useCorporateStore } from '../../services/corporates';
import { showError, showSuccess } from '../../services/toaster';
import {
    ExternalCatalogIntegration,
    ExternalCatalogItemType,
    ExternalCatalogRow,
} from '../../types/externalCatalog';
import { Camp } from '../../types/camps';
import { ICorporate } from '../../types/corporates';
import EditExternalCatalogDialog from '../ExternalCatalog/EditExternalCatalogDialog';

const ROWS_PER_PAGE = 20;

const resolveUploadedFileUrl = (uploadResponse: any): string | undefined => {
    const firstFile = uploadResponse?.data?.data?.files?.[0]
        || uploadResponse?.data?.data?.[0]
        || undefined;

    if (!firstFile) return undefined;
    if (typeof firstFile === 'string') return firstFile;
    if (typeof firstFile === 'object' && typeof firstFile.url === 'string') return firstFile.url;
    return undefined;
};

const renderPreview = (src?: string, fallback?: string) =>
    src ? (
        <CustomImage
            src={src}
            style={{ width: 48, height: 32, borderRadius: 6, objectFit: 'cover' }}
        />
    ) : (
        <Avatar sx={{ width: 48, height: 32, borderRadius: 1 }} variant="rounded">
            {fallback?.charAt(0) || '-'}
        </Avatar>
    );

const PublicExternalCatalog = () => {
    const { fetchAll: fetchCorporates } = useCorporateStore();
    const [integration, setIntegration] = React.useState<ExternalCatalogIntegration>('labstack');
    const [itemType, setItemType] = React.useState<ExternalCatalogItemType>('package');
    const [selectedCorporateId, setSelectedCorporateId] = React.useState('');
    const [corporates, setCorporates] = React.useState<ICorporate[]>([]);
    const [rows, setRows] = React.useState<ExternalCatalogRow[]>([]);
    const [filteredRows, setFilteredRows] = React.useState<ExternalCatalogRow[]>([]);
    const [catalogOptions, setCatalogOptions] = React.useState<ExternalCatalogRow[]>([]);
    const [selectedCatalogItems, setSelectedCatalogItems] = React.useState<ExternalCatalogRow[]>([]);
    const [addOpen, setAddOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [loading, setLoading] = React.useState(false);
    const [loadingCatalogOptions, setLoadingCatalogOptions] = React.useState(false);
    const [adding, setAdding] = React.useState(false);
    const [dialogRow, setDialogRow] = React.useState<ExternalCatalogRow | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [camps, setCamps] = React.useState<Camp[]>([]);

    const loadRows = React.useCallback(async (
        nextItemType: ExternalCatalogItemType,
        nextIntegration: ExternalCatalogIntegration
    ) => {
        setLoading(true);
        try {
            const data = await fetchExternalCatalogRows(
                nextIntegration,
                nextItemType,
                selectedCorporateId || undefined
            );
            setRows(data.filter((row) => Boolean(row.catalogAssignmentId)));
        } finally {
            setLoading(false);
        }
    }, [selectedCorporateId]);

    React.useEffect(() => {
        loadRows(itemType, integration);
    }, [itemType, integration, selectedCorporateId, loadRows]);

    React.useEffect(() => {
        let mounted = true;
        fetchCorporates()
            .then((data) => {
                if (mounted) setCorporates(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (mounted) setCorporates([]);
            });

        return () => {
            mounted = false;
        };
    }, [fetchCorporates]);

    React.useEffect(() => {
        let mounted = true;
        fetchCamps()
            .then((data) => {
                if (mounted) setCamps(data);
            })
            .catch(() => {
                if (mounted) setCamps([]);
            });

        return () => {
            mounted = false;
        };
    }, []);

    React.useEffect(() => {
        const term = search.trim().toLowerCase();
        const next = term
            ? rows.filter((row) =>
                row.name.toLowerCase().includes(term) ||
                row.providerName.toLowerCase().includes(term) ||
                row.externalItemId.toLowerCase().includes(term)
            )
            : rows;
        setFilteredRows(next);
        setCurrentPage(1);
    }, [rows, search]);

    const pagedRows = React.useMemo(() => {
        const start = (currentPage - 1) * ROWS_PER_PAGE;
        return filteredRows.slice(start, start + ROWS_PER_PAGE);
    }, [filteredRows, currentPage]);

    const handleSave = async (value: {
        name?: string;
        customPrice?: number | null;
        customMrp?: number | null;
        markupPercentage?: number | null;
        markupAmount?: number | null;
        imageFile?: File | null;
        coverImageFile?: File | null;
        existingImage?: string;
        existingCoverImage?: string;
        pinned?: boolean;
        enabled?: boolean;
        gender?: 'male' | 'female' | '';
        applicableAge?: number | null;
        campId?: string;
        campIds?: string[];
        skipReport?: boolean;
        skipHealthCertificate?: boolean;
    }) => {
        if (!dialogRow?._id) return;

        setSaving(true);
        try {
            let imageUrl = value.existingImage;
            let coverImageUrl = value.existingCoverImage;

            if (value.imageFile) {
                const uploadResponse = await uploadFile(
                    { module: 'external-bookings', recordId: dialogRow._id },
                    [value.imageFile]
                );
                imageUrl = resolveUploadedFileUrl(uploadResponse) || imageUrl;
            }

            if (value.coverImageFile) {
                const uploadResponse = await uploadFile(
                    { module: 'external-bookings', recordId: dialogRow._id },
                    [value.coverImageFile]
                );
                coverImageUrl = resolveUploadedFileUrl(uploadResponse) || coverImageUrl;
            }

            const updatePayload = omitEmptyOptionalFields({
                name: value.name,
                customPrice: value.customPrice ?? undefined,
                customMrp: value.customMrp ?? undefined,
                markupPercentage: value.markupPercentage ?? undefined,
                markupAmount: value.markupAmount ?? undefined,
                image: imageUrl,
                coverImage: coverImageUrl,
                pinned: value.pinned,
                enabled: value.enabled,
                status: 2 as const,
                gender: dialogRow.itemType === 'package' ? (value.gender || null) : undefined,
                applicableAge: dialogRow.itemType === 'package' ? value.applicableAge : undefined,
                campId: dialogRow.itemType === 'package' && value.campId ? value.campId : undefined,
                campIds: dialogRow.itemType === 'package' ? (value.campIds || []) : undefined,
                skipReport: dialogRow.itemType === 'package' ? value.skipReport : undefined,
                skipHealthCertificate: dialogRow.itemType === 'package' ? value.skipHealthCertificate : undefined,
            });
            if (dialogRow.itemType === 'package' && !value.gender) {
                (updatePayload as any).gender = null;
            }
            if (dialogRow.itemType === 'package' && value.applicableAge === null) {
                (updatePayload as any).applicableAge = null;
            }
            if (value.customPrice === null) {
                (updatePayload as any).customPrice = null;
            }
            if (value.customMrp === null) {
                (updatePayload as any).customMrp = null;
            }
            if (value.markupPercentage === null) {
                (updatePayload as any).markupPercentage = null;
            }
            if (value.markupAmount === null) {
                (updatePayload as any).markupAmount = null;
            }

            if (selectedCorporateId) {
                await updateCorporateCatalogMetadata(dialogRow._id, selectedCorporateId, updatePayload);
            } else {
                await updatePublicCatalogMetadata(dialogRow._id, updatePayload);
            }
            await loadRows(itemType, integration);
            setDialogRow(null);
            showSuccess(selectedCorporateId ? 'Corporate catalog updated' : 'Public catalog updated');
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to update catalog assignment');
        } finally {
            setSaving(false);
        }
    };

    const loadCatalogOptions = React.useCallback(async () => {
        setLoadingCatalogOptions(true);
        try {
            const masterRows = await fetchExternalCatalogRows(
                integration,
                itemType,
                undefined,
                true
            );
            const assignedIds = new Set(rows.map((row) => row._id).filter(Boolean));
            setCatalogOptions(
                masterRows.filter(
                    (row) =>
                        row._id &&
                        row.enabled &&
                        row.status === 2 &&
                        !assignedIds.has(row._id)
                )
            );
        } catch (error) {
            setCatalogOptions([]);
            showError(error instanceof Error ? error.message : 'Failed to load catalog items');
        } finally {
            setLoadingCatalogOptions(false);
        }
    }, [integration, itemType, rows]);

    React.useEffect(() => {
        if (addOpen) {
            loadCatalogOptions();
        }
    }, [addOpen, loadCatalogOptions]);

    const handleAddAssignment = async () => {
        const selected = selectedCatalogItems.filter((row) => row._id);
        if (!selected.length) return;

        setAdding(true);
        try {
            const payload = {
                enabled: true,
                status: 2 as const,
            };
            const results = await Promise.allSettled(selected.map((item) =>
                selectedCorporateId
                    ? updateCorporateCatalogMetadata(item._id!, selectedCorporateId, payload)
                    : updatePublicCatalogMetadata(item._id!, payload)
            ));
            const failed = selected.filter((_item, index) => {
                const result = results[index];
                return result.status !== 'fulfilled' || !result.value;
            });
            const addedCount = selected.length - failed.length;

            if (addedCount) await loadRows(itemType, integration);
            if (failed.length) {
                setSelectedCatalogItems(failed);
                showError(`${addedCount} added; ${failed.length} failed. Retry the remaining items.`);
            } else {
                setSelectedCatalogItems([]);
                setAddOpen(false);
                showSuccess(`${addedCount} ${itemType === 'package' ? 'package' : 'test'} assignment${addedCount === 1 ? '' : 's'} added`);
            }
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to assign catalog item');
        } finally {
            setAdding(false);
        }
    };

    const columns = React.useMemo(() => [
        {
            header: 'S.No.',
            accessor: '_index',
            render: (_row: any, index?: number) => (currentPage - 1) * ROWS_PER_PAGE + (index ?? 0) + 1,
        },
        {
            header: 'Cover',
            accessor: 'coverImage',
            render: (row: ExternalCatalogRow) => renderPreview(row.coverImage, row.name || row.providerName),
        },
        {
            header: 'Image',
            accessor: 'image',
            render: (row: ExternalCatalogRow) => renderPreview(row.image, row.name || row.providerName),
        },
        {
            header: 'Name',
            accessor: 'name',
            render: (row: ExternalCatalogRow) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.name || row.providerName || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Provider: {row.providerName || '-'}
                    </Typography>
                </Box>
            ),
        },
        { header: 'External ID', accessor: 'externalItemId' },
        {
            header: 'Provider Price',
            accessor: 'price',
            render: (row: ExternalCatalogRow) => row.price != null ? `Rs. ${row.price}` : '-',
        },
        {
            header: 'Custom Price',
            accessor: 'customPrice',
            render: (row: ExternalCatalogRow) => row.customPrice != null ? `Rs. ${row.customPrice}` : '-',
        },
        {
            header: 'Custom MRP',
            accessor: 'customMrp',
            render: (row: ExternalCatalogRow) => row.customMrp != null ? `Rs. ${row.customMrp}` : '-',
        },
        {
            header: 'Applicable Age',
            accessor: 'applicableAge',
            render: (row: ExternalCatalogRow) => row.itemType === 'package' && row.applicableAge != null
                ? `${row.applicableAge}+ years`
                : 'All ages',
        },
        {
            header: 'Pinned',
            accessor: 'pinned',
            render: (row: ExternalCatalogRow) => (
                <Chip
                    label={row.pinned ? 'Pinned' : 'Normal'}
                    color={row.pinned ? 'primary' : 'default'}
                    size="small"
                />
            ),
        },
        {
            header: 'Visibility',
            accessor: 'enabled',
            render: (row: ExternalCatalogRow) => (
                <Chip
                    label={row.enabled ? 'Enabled' : 'Disabled'}
                    color={row.enabled ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            header: 'Updated',
            accessor: 'updatedAt',
            render: (row: ExternalCatalogRow) => row.updatedAt ? moment(row.updatedAt).format('DD-MM-YYYY') : '-',
        },
        {
            header: 'Edit',
            accessor: 'edit',
            render: (row: ExternalCatalogRow) => (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setDialogRow(row)}
                >
                    Edit
                </Button>
            ),
        },
    ], [currentPage]);

    return (
        <Layout appBarTitle="Catalog Assignment">
            <Box sx={{ display: 'grid', gap: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                        <Typography variant="h6">Catalog Assignments</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage public and corporate-specific external catalog assignments.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <FormControl size="small" sx={{ minWidth: 220, backgroundColor: '#fff' }}>
                            <InputLabel>Catalog Scope</InputLabel>
                            <Select
                                label="Catalog Scope"
                                value={selectedCorporateId}
                                onChange={(e) => {
                                    setSelectedCorporateId(e.target.value);
                                    setSelectedCatalogItems([]);
                                    setAddOpen(false);
                                }}
                            >
                                <MenuItem value="">Public Catalog</MenuItem>
                                {corporates.map((corporate) => (
                                    <MenuItem key={corporate._id} value={corporate._id || ''}>
                                        {corporate.name}
                                        {corporate.companyName ? ` - ${corporate.companyName}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 160, backgroundColor: '#fff' }}>
                            <InputLabel>Provider</InputLabel>
                            <Select
                                label="Provider"
                                value={integration}
                                onChange={(e) => {
                                    setIntegration(e.target.value as ExternalCatalogIntegration);
                                    setSelectedCatalogItems([]);
                                }}
                            >
                                <MenuItem value="labstack">Labstack</MenuItem>
                                <MenuItem value="healthians">Healthians</MenuItem>
                                <MenuItem value="betacura">BetaCura</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setSelectedCatalogItems([]);
                                setCatalogOptions([]);
                                setAddOpen(true);
                            }}
                        >
                            Add
                        </Button>
                    </Stack>
                </Stack>

                <Tabs
                    value={itemType}
                    onChange={(_e, value) => {
                        setItemType(value);
                        setSelectedCatalogItems([]);
                    }}
                    sx={{ backgroundColor: '#fff', borderRadius: 2, px: 1 }}
                >
                    <Tab label="Packages" value="package" />
                    <Tab label="Tests" value="test" />
                </Tabs>

                <Box
                    component="input"
                    value={search}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    placeholder={`Search ${selectedCorporateId ? 'corporate' : 'public'} ${itemType}s by name or external ID`}
                    sx={{
                        border: '1px solid #d0d7de',
                        borderRadius: 2,
                        px: 2,
                        py: 1.2,
                        fontSize: 14,
                        backgroundColor: '#fff',
                    }}
                />

                <GridTable
                    data={pagedRows}
                    columns={columns}
                    currentPage={currentPage}
                    totalPages={Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE))}
                    total={filteredRows.length}
                    loading={loading}
                    onPageChange={(_event, page) => setCurrentPage(page + 1)}
                    module={MODULES.NOTIFICATION}
                    rows={ROWS_PER_PAGE}
                    hideAction
                />
            </Box>

            <EditExternalCatalogDialog
                open={Boolean(dialogRow)}
                row={dialogRow}
                camps={camps}
                saving={saving}
                corporateScope={Boolean(selectedCorporateId)}
                showBookingDefaults
                onClose={() => setDialogRow(null)}
                onSubmit={handleSave}
            />

            <Dialog
                open={addOpen}
                onClose={() => {
                    if (!adding) {
                        setAddOpen(false);
                        setSelectedCatalogItems([]);
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Add {itemType === 'package' ? 'Package' : 'Test'} Assignments
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        limitTags={2}
                        options={catalogOptions}
                        loading={loadingCatalogOptions}
                        value={selectedCatalogItems}
                        onChange={(_event, items) => setSelectedCatalogItems(items)}
                        getOptionLabel={(option) => `${option.name || option.providerName || option.externalItemId} (${option.externalItemId})`}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        noOptionsText={catalogOptions.length ? 'No matching items' : 'No approved unassigned items available'}
                        loadingText="Loading catalog items..."
                        disabled={adding}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option._id}>
                                <Checkbox checked={selected} sx={{ mr: 1 }} />
                                <Box>
                                    <Typography variant="body2">{option.name || option.providerName || option.externalItemId}</Typography>
                                    <Typography variant="caption" color="text.secondary">ID: {option.externalItemId}</Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={`Search approved ${itemType}s`}
                                placeholder={selectedCatalogItems.length ? '' : 'Search by name or external ID'}
                                size="small"
                                sx={{ mt: 1 }}
                            />
                        )}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                        {selectedCatalogItems.length} selected. Only approved, enabled, unassigned items are shown.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setAddOpen(false);
                            setSelectedCatalogItems([]);
                        }}
                        disabled={adding}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddAssignment}
                        disabled={!selectedCatalogItems.length || adding}
                    >
                        {adding ? 'Adding...' : `Add ${selectedCatalogItems.length} assignment${selectedCatalogItems.length === 1 ? '' : 's'}`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default PublicExternalCatalog;
